import { c as changeItemQuantity, a as contexts } from './cart-cce84c00.js';
import { E as EVENTS } from './events-58bc9098.js';

const __spd = (tag, ...args) => { try { if (localStorage.getItem('sp_cart_debug') === '1' || /[?&]debugCart=1/i.test(location.search)) { localStorage.setItem('sp_cart_debug','1'); console.log(`%c[SP/${tag}]%c +${(performance.now()/1000).toFixed(3)}s`, 'color:#4C6CF8;font-weight:bold', 'color:#888', ...args); } } catch {} };

class CartItem extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      changePending: "change-pending",
      deletePending: "delete-pending",
    };

    this.selectors = {
      errorDisplay: "[js-error-display]",
      deleteButton: "[js-delete-button]",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();

    const eventOptions = {
      signal: this.controller.signal,
    };

    this.cartItemKey = this.dataset.cartItemKey;

    // Because the cart re-renders cart items on any update, we can
    // use the data attribute to know the last valid quantity.  This
    // is necessary to revert things on quantity change error
    this.lastConfirmedQuantity = this.dataset.cartItemQuantity;

    this.quantityInput = this.querySelector("quantity-input");
    this.quantityInput.addEventListener(
      EVENTS.QUANTITY_INPUT_CHANGE,
      (event) => {
        this.dispatchEvent(
          new CustomEvent(EVENTS.CART_ITEM_CHANGE_PENDING, {
            bubbles: true,
          }),
        );

        this.classList.add(this.classes.changePending);
        changeItemQuantity(
          this,
          this.cartItemKey,
          event.detail.quantity,
          contexts.cartDrawer,
        );
      },
      eventOptions,
    );

    this.deleteButton = this.querySelector(this.selectors.deleteButton);

    this.deleteButton.addEventListener(
      "click",
      () => {
        __spd('delete.click', {
          domCartItemKey: this.dataset.cartItemKey,
          domCartItemId: this.dataset.cartItemId,
          domQuantity: this.dataset.cartItemQuantity,
          wsDogName: this.dataset.wsDogName,
        });
        this.classList.add(this.classes.deletePending);
        this.dispatchEvent(
          new CustomEvent(EVENTS.CART_ITEM_CHANGE_PENDING, {
            bubbles: true,
          }),
        );

        /* Same delete the charm bundles use — changeItemQuantity() — but with a key
         * resolved from the CURRENT cart first.
         *
         * Charm bundles delete fine because their key is still valid; nothing in the
         * theme is bundle-aware. Our set bundle fails because apps rewrite line
         * properties after add, which changes the line key, so the key rendered into the
         * DOM is stale by the time it is clicked and Shopify answers "no valid id or
         * line parameter". A merged bundle is a single line keyed on the parent variant,
         * so deleting it removes every component (verified against the live cart).
         *
         * Resolve, then hand off to the theme's own path so the drawer updates normally. */
        const domKey = String(this.cartItemKey || '');
        const variantPrefix = domKey.split(':')[0];
        const detailsAttr = this.dataset.bundleDetails || '';

        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const items = cart.items || [];
            __spd('delete.cartKeys', {
              domKey,
              lineCount: items.length,
              keys: items.map((it) => ({
                key: it.key,
                variant: it.variant_id,
                bundleId: (it.properties || {}).bundleId,
              })),
            });

            // 1. exact match — an ordinary line
            let targets = items.filter((it) => String(it.key) === domKey);
            let how = 'exact';

            /* 2. the row is a MERGED bundle: its key belongs to the parent variant and is
             *    not a real line. Remove every real line in that bundle group.
             *
             * Match on bundleId FIRST. _Details lists only what the Function chose to
             * aggregate, so a group member that is not in it — the $0 mirror units of a
             * volume tier, for one — survived the delete and the customer had to hit the
             * bin two or three times to clear one bundle. Every line of a bundle carries
             * the same bundleId, so that is the honest grouping key; _Details stays as the
             * fallback for older lines that predate it. */
            const groupId = String(this.dataset.bundleId || '');
            const detailIds = (detailsAttr.match(/\d{6,}/g) || []).map(Number);
            const inGroup = (it) => {
              if (groupId && String((it.properties || {}).bundleId || '') === groupId) return true;
              return detailIds.length ? detailIds.includes(Number(it.variant_id)) : false;
            };
            if (!targets.length) {
              targets = items.filter(inGroup);
              if (targets.length) how = groupId ? 'bundleId' : 'details';
            }

            // 3. last resort — unambiguous same-variant line
            if (!targets.length) {
              const same = items.filter((it) => String(it.key).split(':')[0] === variantPrefix);
              if (same.length === 1) { targets = same; how = 'prefix'; }
            }

            __spd('delete.resolved', { how, count: targets.length, keys: targets.map((t) => t.key) });
            if (!targets.length) throw new Error('no matching cart line');

            if (targets.length === 1 && how !== 'details') {
              this.cartItemKey = String(targets[0].key);
              changeItemQuantity(this, this.cartItemKey, 0, contexts.cartDrawer);
              return;
            }

            /* Multiple real lines behind one row. Zero all but ONE quietly, then let the
             * theme delete the last via changeItemQuantity — that is what re-renders the
             * drawer. Reloading the page instead worked but threw away the drawer. */
            const keys = targets.map((t) => String(t.key));
            const lastKey = keys[keys.length - 1];
            const rest = keys.slice(0, -1);

            const zeroRest = rest.length
              ? fetch('/cart/update.js', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({
                    updates: rest.reduce((acc, k) => { acc[k] = 0; return acc; }, {}),
                  }),
                }).then((res) => res.json())
              : Promise.resolve(null);

            /* Re-read the cart before handing the last line to the theme. Zeroing lines
             * re-keys the ones that remain — apps rewrite properties on every cart change —
             * so the key captured a moment ago is stale, the final delete fails, and the
             * bundle is left half-removed. Resolve the survivor by GROUP, not by key. */
            return zeroRest
              .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }))
              .then((r) => r.json())
              .then((fresh) => {
                const survivor = (fresh.items || []).find(inGroup);
                const key = survivor ? String(survivor.key) : lastKey;
                __spd('delete.groupDone', {
                  zeroed: rest.length, handOff: key, restaked: !!survivor });
                this.cartItemKey = key;
                changeItemQuantity(this, key, 0, contexts.cartDrawer);
              });
          })
          .catch((e) => {
            __spd('delete.error', String(e));
            changeItemQuantity(this, this.cartItemKey, 0, contexts.cartDrawer);
          });
        return;

        changeItemQuantity(
          this,
          this.cartItemKey,
          0,
          contexts.cartDrawer,
        );
      },
      eventOptions,
    );

    this.addEventListener(
      EVENTS.CART_ITEM_CHANGE_ERROR,
      (event) => {
        __spd('delete.error.received', {
          domKey: this.cartItemKey,
          eventKey: event.detail.cartItemKeyOrId,
          matched: this.cartItemKey === event.detail.cartItemKeyOrId,
          errorMessage: event.detail.errorMessage,
        });
        if (this.cartItemKey === event.detail.cartItemKeyOrId) {
          this.#displayErrorMessage(event.detail.errorMessage);
          this.classList.remove(this.classes.changePending);
          if (this.lastConfirmedQuantity !== undefined) {
            this.quantityInput.setQuantity(this.lastConfirmedQuantity);
          }
        }
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  #displayErrorMessage(message) {
    this.errorDisplay = this.querySelector(this.selectors.errorDisplay);
    this.errorDisplay.hidden = false;
    this.errorDisplay.innerText = message;
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}
